import React, { useMemo, useState } from "react";

import { ChevronDownIcon } from "@packages/ds";
import { useIntl } from "react-intl";
import styled from "styled-components";

import { Link } from "ds/Button";
import useDecodedParams from "Hooks/useDecodedParams";

import { Popover } from "./Popover";

type Project = {
  projectId: string;
  projectName: string;
  totalEmissions: number | null;
};

type SortField = "name" | "emissions";

type EmissionsPerProjectTableProps = {
  projectsTableData: Project[];
  year: number;
};

export const EmissionsPerProjectTable: React.FC<
  EmissionsPerProjectTableProps
> = ({ projectsTableData, year }) => {
  const intl = useIntl();
  const { organizationId } = useDecodedParams();

  const [sortField, setSortField] = useState<SortField>("emissions");

  const handleSortClick = (field: SortField) => {
    setSortField(field);
  };

  const sortedData = useMemo(() => {
    if (!projectsTableData.length) return [];

    const data = [...projectsTableData];

    if (sortField === "name") {
      // Sort by name (ascending)
      return data.sort((a, b) => a.projectName.localeCompare(b.projectName));
    } else {
      // Sort by emissions (descending)
      return data.sort((a, b) => {
        // Handle null values in sorting
        if (a.totalEmissions === null && b.totalEmissions === null) return 0;
        if (a.totalEmissions === null) return 1; // Nulls go to the bottom
        if (b.totalEmissions === null) return -1;
        return b.totalEmissions - a.totalEmissions;
      });
    }
  }, [projectsTableData, sortField]);

  return (
    <TableContainer>
      {projectsTableData.length > 0 ? (
        <>
          <TableHeaderRow>
            <ColumnHeader onClick={() => handleSortClick("name")}>
              {intl.formatMessage({
                id: "carbon.emissions.projects.projectname",
                defaultMessage: "Project name"
              })}
              <SortIcon>
                <Chevron $active={sortField === "name"} />
              </SortIcon>
            </ColumnHeader>
            <ColumnHeaderRight onClick={() => handleSortClick("emissions")}>
              {intl.formatMessage({
                id: "carbon.emissions.projects.yeartotal",
                defaultMessage: "Year total"
              })}
              <SortIcon>
                <Chevron $active={sortField === "emissions"} />
              </SortIcon>
            </ColumnHeaderRight>
          </TableHeaderRow>

          {sortedData.map(project => (
            <TableRow key={project.projectId}>
              <ProjectNameCell>
                <Link to={`/${organizationId}/${project.projectId}`}>
                  {project.projectName}
                </Link>
              </ProjectNameCell>
              <EmissionsCell>
                {project.totalEmissions !== null ? (
                  // Show emissions value if data is available
                  <>
                    <EmissionsValue>
                      {formatNumber(project.totalEmissions)}
                    </EmissionsValue>
                    <EmissionsUnit>
                      {intl.formatMessage({
                        id: "carbon.emissions.units",
                        defaultMessage: "kg CO2e"
                      })}
                    </EmissionsUnit>
                  </>
                ) : (
                  // Show "No data" with info icon for projects with no data
                  <NoDataContainer>
                    <NoDataText>
                      {intl.formatMessage({
                        id: "carbon.emissions.projects.nodata.individual",
                        defaultMessage: "No data"
                      })}
                    </NoDataText>
                    <Popover />
                  </NoDataContainer>
                )}
              </EmissionsCell>
            </TableRow>
          ))}
        </>
      ) : (
        <NoDataMessage>
          {intl.formatMessage(
            {
              id: "carbon.emissions.projects.nodata",
              defaultMessage: "No project data available"
            },
            { year }
          )}
          <Popover />
        </NoDataMessage>
      )}
    </TableContainer>
  );
};

// Helper for formatting numbers
const formatNumber = (value: number | undefined) => {
  return value !== undefined ? value.toFixed(2) : "0.00";
};

const TableContainer = styled.div`
  width: 100%;
  overflow: hidden;
`;

const TableHeaderRow = styled.div`
  display: flex;
  padding: 4px 8px;
  border-bottom: 1px solid var(--mode-border-neutral-weakest);
`;

const ColumnHeader = styled.div`
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  color: var(--mode-text-neutral-weakest);
  display: flex;
  align-items: center;
  cursor: pointer;

  &:hover {
    color: var(--mode-text-primary-default);
  }
`;

const ColumnHeaderRight = styled(ColumnHeader)`
  justify-content: flex-end;
`;

const SortIcon = styled.span`
  margin-left: 4px;
  font-size: 12px;
`;

const Chevron = styled(ChevronDownIcon)<{ $active?: boolean }>`
  color: ${props =>
    props.$active
      ? "var(--mode-vector-primary-default)"
      : "var(--mode-vector-neutral-default)"};
  vertical-align: text-bottom;

  &:hover {
    color: var(--mode-vector-primary-default);
  }
`;

const TableRow = styled.div`
  display: flex;
  padding: 12px 8px;
  border-bottom: 1px solid var(--mode-border-neutral-weakest);

  &:last-child {
    border-bottom: none;
  }
`;

const ProjectNameCell = styled.div`
  flex: 1;
`;

const EmissionsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
`;

const EmissionsValue = styled.span`
  color: var(--mode-text-neutral-default);
  font-weight: 600;
  font-size: 15px;
`;

const EmissionsUnit = styled.span`
  color: var(--mode-text-neutral-weakest);
  font-size: 13px;
  font-weight: 600;
`;

const NoDataContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const NoDataText = styled.span`
  color: var(--mode-text-neutral-weakest);
  font-size: 13px;
  top: -2px;
  position: relative;
`;

const NoDataMessage = styled.div`
  padding: 24px;
  text-align: center;
  color: var(--mode-text-neutral-weakest);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;
